import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Checkbox,
  Drawer,
  FileField,
  Input,
  Field,
  Select,
  Alert
} from '@itds/elements/index.js';

import { Api } from '@brokerage/utils/Api';

const AddConfigField = ({ onClose, onSave, show }) => {
  const [data, setData] = useState({
    boolean_default: false,
    decimal_default: null,
    decimal_precision: null,
    integer_default: null,
    array_defaults: [],
    field_type: 'text',
    guid: null,
    label: '',
    name: '',
    text_default: '',
    versions: [],
    array_options: [],
    textarea_default: '',
    group: '',
    file_default: null
  });

  const [errors, setErrors] = useState([]);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Debug current field type
  useEffect(() => {
    // Reset any field-specific errors when field type changes
    if (submitAttempted) {
      setErrors(errors.filter(error => ['name', 'label', 'group', 'field_type'].includes(error)));
    }
  }, [data.field_type]);

  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'textarea', label: 'Textarea' },
    { value: 'integer', label: 'Integer' },
    { value: 'decimal', label: 'Decimal' },
    { value: 'boolean', label: 'Boolean' },
    { value: 'array', label: 'Array' },
    { value: 'dropdown', label: 'Dropdown' } //,
    // { value: 'file', label: 'File' }
  ];

  const handleInputChange = (name, e) => {
    setData({ ...data, [name]: e.target.value });

    if (submitAttempted && errors.includes(name)) {
      setErrors(errors.filter(error => error !== name));
    }
  };

  const handleCheckboxChange = (name, e) => {
    setData({ ...data, [name]: e.target.checked });
  };

  const handleFileChange = (name, file) => {
    setData({ ...data, [name]: file });

    if (submitAttempted && errors.includes('file_default')) {
      setErrors(errors.filter(error => error !== 'file_default'));
    }
  };

  const handleVersionsChange = e => {
    const versions = e.target.value.split(',').map(version => version.trim());

    setData({ ...data, versions });
  };

  const validateForm = () => {
    const newErrors = [];

    // Always required fields
    if (!data.name.trim()) {
      newErrors.push('name');
    }

    if (!data.label.trim()) {
      newErrors.push('label');
    }

    if (!data.group.trim()) {
      newErrors.push('group');
    }

    if (!data.field_type) {
      newErrors.push('field_type');
    }

    // Validate fields based on selected field type
    switch (data.field_type) {
      case 'text':
        if (!data.text_default.trim()) {
          newErrors.push('text_default');
        }
        break;
      case 'textarea':
        if (!data.textarea_default.trim()) {
          newErrors.push('textarea_default');
        }
        break;
      case 'integer':
        if (data.integer_default === null || data.integer_default === '') {
          newErrors.push('integer_default');
        }
        break;
      case 'decimal':
        if (data.decimal_precision === null || data.decimal_precision === '') {
          newErrors.push('decimal_precision');
        }
        if (data.decimal_default === null || data.decimal_default === '') {
          newErrors.push('decimal_default');
        }
        break;
      case 'array':
        if (!data.array_options.length) {
          newErrors.push('array_options');
        }
        break;
      case 'file':
        if (!data.file_default) {
          newErrors.push('file_default');
        }
        break;
      default:
        // No additional validation for other types
        break;
    }

    setErrors(newErrors);
    setSubmitAttempted(true);

    return newErrors.length === 0;
  };

  const save = () => {
    if (!validateForm()) {
      return;
    }

    // Convert string values to appropriate types
    const payload = {
      ...data,
      integer_default:
        data.integer_default !== null && data.integer_default !== '' ?
          parseInt(data.integer_default, 10) :
          null,
      decimal_default:
        data.decimal_default !== null && data.decimal_default !== '' ?
          parseFloat(data.decimal_default) :
          null,
      decimal_precision:
        data.decimal_precision !== null && data.decimal_precision !== '' ?
          parseFloat(data.decimal_precision) :
          null
    };

    Api.createConfigField(payload).then(response => {
      // Pass the field data back to the parent component
      const fieldData = response && response.data ? response.data : payload;

      onSave(fieldData);

      setSubmitAttempted(false);
      setErrors([]);
      setData({
        boolean_default: false,
        decimal_default: null,
        decimal_precision: null,
        integer_default: null,
        array_defaults: [],
        field_type: 'text',
        guid: null,
        label: '',
        name: '',
        text_default: '',
        versions: [],
        array_options: [],
        textarea_default: '',
        group: '',
        file_default: null
      });
    });
  };

  const handleFieldTypeChange = event => {
    const selectedValue = event.target.value;

    setData({
      ...data,
      field_type: selectedValue
    });

    if (submitAttempted && errors.includes('field_type')) {
      setErrors(errors.filter(error => error !== 'field_type'));
    }
  };

  return (
    <Drawer
      alignRight={true}
      footer={<Button onClick={save}>Create Config Field</Button>}
      onClose={onClose}
      show={show}
      style={{ maxHeight: '90vh', overflowY: 'auto' }}
      title='Add New Config Field'
    >
      {errors.length > 0 && submitAttempted && (
        <Alert style={{ marginBottom: '15px' }} type='error'>
          Please fill in all required fields.
        </Alert>
      )}

      <Field label='Name'>
        <Input
          invalid={errors.includes('name')}
          onChange={handleInputChange.bind(null, 'name')}
          required={true}
          value={data.name}
        />
      </Field>

      <Field label='Group'>
        <Input
          invalid={errors.includes('group')}
          onChange={handleInputChange.bind(null, 'group')}
          required={true}
          value={data.group}
        />
      </Field>

      <Field label='Label'>
        <Input
          invalid={errors.includes('label')}
          onChange={handleInputChange.bind(null, 'label')}
          required={true}
          value={data.label}
        />
      </Field>

      <Field label='Field Type' required={true}>
        <Select
          invalid={errors.includes('field_type')}
          onChange={handleFieldTypeChange}
          options={fieldTypes}
          value={data.field_type}
        />
      </Field>

      <Field label='Versions (comma separated)'>
        <Input
          onChange={handleVersionsChange}
          placeholder='e.g. 1.0.0, 2.0.0'
          value={data.versions.join(', ')}
        />
      </Field>

      <Field label='Default Value'>
        {data.field_type === 'text' ? (
          <Input
            invalid={errors.includes('text_default')}
            onChange={handleInputChange.bind(null, 'text_default')}
            required={true}
            value={data.text_default}
          />
        ) : null}

        {data.field_type === 'textarea' ? (
          <Input
            height={120}
            invalid={errors.includes('textarea_default')}
            onChange={handleInputChange.bind(null, 'textarea_default')}
            required={true}
            type='textarea'
            value={data.textarea_default}
          />
        ) : null}

        {data.field_type === 'integer' ? (
          <Input
            invalid={errors.includes('integer_default')}
            onChange={handleInputChange.bind(null, 'integer_default')}
            required={true}
            step={1}
            type='number'
            value={data.integer_default || ''}
          />
        ) : null}

        {data.field_type === 'decimal' ? (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ width: '49%' }}>
              <Field label='Precision'>
                <Input
                  invalid={errors.includes('decimal_precision')}
                  onChange={handleInputChange.bind(null, 'decimal_precision')}
                  required={true}
                  step={1}
                  type='number'
                  value={data.decimal_precision || ''}
                />
              </Field>
            </div>
            <div style={{ width: '49%' }}>
              <Field label='Default'>
                <Input
                  invalid={errors.includes('decimal_default')}
                  onChange={handleInputChange.bind(null, 'decimal_default')}
                  required={true}
                  type='number'
                  value={data.decimal_default || ''}
                />
              </Field>
            </div>
          </div>
        ) : null}

        {data.field_type === 'boolean' ? (
          <Checkbox
            checked={data.boolean_default}
            label='Boolean Default Value'
            onChange={handleCheckboxChange.bind(null, 'boolean_default')}
          />
        ) : null}

        {data.field_type === 'array' || data.field_type === 'dropdown' ? (
          <Field label='Array Options (comma separated)'>
            <Input
              invalid={errors.includes('array_options')}
              onChange={e => {
                const options = e.target.value.split(',').map(option => option.trim());

                // Set both array_options and array_defaults to the same value
                setData({
                  ...data,
                  array_options: options,
                  array_defaults: options // Use the same options as defaults
                });

                if (submitAttempted && errors.includes('array_options')) {
                  setErrors(errors.filter(error => error !== 'array_options'));
                }
              }}
              placeholder='e.g. option1, option2, option3'
              required={true}
              value={data.array_options.join(', ')}
            />
          </Field>
        ) : null}

        {data.field_type === 'file' ? (
          <FileField
            file={data.file_default}
            fileUrl=''
            onChange={handleFileChange.bind(null, 'file_default')}
            required={true}
          />
        ) : null}
      </Field>
    </Drawer>
  );
};

AddConfigField.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired
};

export default AddConfigField;
